// Purpose: standard page header slot for the AppShell. Renders an
// optional breadcrumb, a display-m title, an optional subtitle, and
// an actions slot on the right.
//
// Breadcrumb is passed as children nodes in a dedicated `breadcrumb`
// slot — callers typically build it from <Breadcrumb> primitives that
// are just spans with a ChevronRight separator (see Breadcrumb below).

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '../icons/index.tsx';

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional breadcrumb node rendered above the title. */
  breadcrumb?: ReactNode;
  /** Right-aligned actions (typically Buttons). */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ title, subtitle, breadcrumb, actions, className }: PageHeaderProps) {
  return (
    <header
      className={['flex items-end justify-between gap-6', 'flex-wrap', className ?? ''].join(' ')}
    >
      <div className="flex flex-col gap-2 min-w-0">
        {breadcrumb ? <div>{breadcrumb}</div> : null}
        <h1 className="font-display text-display-m text-fg-primary [font-feature-settings:var(--font-feature-display)]">
          {title}
        </h1>
        {subtitle ? <p className="text-body-l text-fg-secondary">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2 flex-shrink-0">{actions}</div> : null}
    </header>
  );
}

export interface BreadcrumbItem {
  label: ReactNode;
  to?: string;
}

export function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-body-s text-fg-muted">
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <span key={idx} className="flex items-center gap-2">
            {item.to && !isLast ? (
              <Link
                to={item.to}
                className="hover:text-fg-primary transition-colors duration-fast ease-out"
              >
                {item.label}
              </Link>
            ) : (
              <span className={isLast ? 'text-fg-secondary' : ''}>{item.label}</span>
            )}
            {!isLast ? <ChevronRightIcon size={12} aria-hidden /> : null}
          </span>
        );
      })}
    </nav>
  );
}
